import Image from "next/image";
import Link from "next/link";
import { Github, Linkedin, type LucideIcon } from "lucide-react";

type FooterLink = Readonly<{
  href: string;
  label: string;
}>;

type SocialLink = FooterLink &
  Readonly<{
    icon: LucideIcon;
  }>;

const FOOTER_LINKS: readonly FooterLink[] = [
  { href: "/dongl", label: "Dongl" },
  {
    href: "https://simpleble.org/?utm_source=simpleble_store&utm_medium=store&utm_campaign=store_footer",
    label: "Main site",
  },
  {
    href: "https://docs.simpleble.org/?utm_source=simpleble_store&utm_medium=store&utm_campaign=store_footer",
    label: "Docs",
  },
  { href: "https://github.com/simpleble/simpleble", label: "GitHub" },
  { href: "/returns", label: "Returns & FAQ" },
  { href: "/privacy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
];

const SOCIAL_LINKS: readonly SocialLink[] = [
  {
    href: "https://github.com/simpleble/simpleble",
    label: "GitHub",
    icon: Github,
  },
  {
    href: "https://www.linkedin.com/company/simpleble/",
    label: "LinkedIn",
    icon: Linkedin,
  },
];

const Footer = (): React.ReactElement => {
  return (
    <footer className="bg-primary relative overflow-hidden px-6 py-14 text-white md:px-16">
      {/* Background image with soft-light blend, matching the main site */}
      <div className="absolute inset-0 mix-blend-soft-light">
        <Image
          src="/images/footer-bg.png"
          alt=""
          fill
          className="scale-y-[-1] rotate-180 object-cover"
        />
      </div>

      <div className="relative z-10 mx-auto flex max-w-[1312px] flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div className="flex max-w-md flex-col gap-4">
          <Image
            src="/images/logos/simpleBLE-logo.png"
            alt="SimpleBLE"
            width={152}
            height={50}
            className="h-[50px] w-[152px]"
          />
          <p className="font-sora text-sm leading-6 text-white/80">
            Official gear for the developers and makers shipping cross-platform
            Bluetooth with SimpleBLE.
          </p>
        </div>

        <div className="flex flex-col gap-6 md:items-end">
          <nav
            className="flex flex-wrap gap-x-5 gap-y-3"
            aria-label="Footer navigation"
          >
            {FOOTER_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="font-sora text-sm font-semibold text-white/80 transition-colors hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {SOCIAL_LINKS.map((social) => {
              const Icon = social.icon;

              return (
                <Link
                  key={social.href}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10"
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </Link>
              );
            })}
          </div>

          <span className="font-sora text-sm leading-6 text-white/70">
            &copy; {new Date().getFullYear()} SimpleBLE by{" "}
            <Link
              href="https://californiaopensource.com/?utm_source=simpleble_store&utm_medium=store&utm_campaign=store_footer"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-white hover:underline"
            >
              The California Open Source Company
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
